import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    els.forEach((el) => el.classList.remove('visible'));
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -20px 0px' }
    );
    const timer = setTimeout(() => els.forEach((el) => observer.observe(el)), 50);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, []);
}

const serviceCategories = [
  {
    title: 'Retail Pharmacy',
    badge: 'Core Business',
    badgeColor: 'bg-primary-100 text-primary-700',
    desc: 'Our flagship APlus Pharmacy outlets are modern, well-stocked retail pharmacies offering a complete range of medicines, wellness products, and healthcare essentials.',
    features: ['Prescription medicines', 'OTC (Over-the-counter) drugs', 'Generic medicines at up to 80% savings', 'Trained pharmacist consultation', 'Digital billing & e-prescriptions', '24x7 select outlets'],
    gradient: 'from-primary-600 to-primary-800',
  },
  {
    title: 'Generic Medicines',
    badge: 'Affordable Care',
    badgeColor: 'bg-green-100 text-green-700',
    desc: 'We are strong advocates of generic medicines. Our stores maintain India\'s largest range of WHO-GMP certified generic medicines to help patients save significantly on healthcare costs.',
    features: ['WHO-GMP certified generics', 'Up to 80% cost savings', 'Bioequivalent to branded drugs', 'Availability of Jan Aushadhi products', 'Doctor-prescribed generics honored', 'Transparent pricing displayed'],
    gradient: 'from-secondary-600 to-secondary-800',
  },
  {
    title: 'Health & Wellness Products',
    badge: 'Wellness',
    badgeColor: 'bg-amber-100 text-amber-700',
    desc: 'Beyond medicines, APlus Pharmacy is a one-stop health destination offering a wide range of wellness, nutrition, and personal care products.',
    features: ['Nutraceuticals & supplements', 'Protein & sports nutrition', 'Baby & mother care products', 'Skincare & cosmetics', 'Herbal & ayurvedic products', 'Medical devices & equipment'],
    gradient: 'from-amber-500 to-orange-600',
  },
  {
    title: 'Diagnostic Services',
    badge: 'Diagnostics',
    badgeColor: 'bg-rose-100 text-rose-700',
    desc: 'We partner with leading diagnostic laboratories to offer convenient, affordable diagnostic tests at our pharmacy outlets, bringing testing closer to patients.',
    features: ['Blood tests & panels', 'Diabetes monitoring', 'Blood pressure checks', 'BMI & health screenings', 'Tie-up with certified labs', 'Home sample collection available'],
    gradient: 'from-rose-600 to-pink-700',
  },
  {
    title: 'Home Healthcare',
    badge: 'Home Care',
    badgeColor: 'bg-violet-100 text-violet-700',
    desc: 'Our home healthcare services bring pharmacy and wellness support directly to patients\' homes — especially for elderly patients and those with chronic conditions.',
    features: ['Medicine home delivery', 'Nursing & caretaker services', 'Medical equipment rental', 'Post-surgery care products', 'Chronic disease management', 'Elder care essentials'],
    gradient: 'from-violet-600 to-purple-700',
  },
  {
    title: 'Digital Health Platform',
    badge: 'Technology',
    badgeColor: 'bg-blue-100 text-blue-700',
    desc: 'Our digital platform enables customers to order medicines online, upload prescriptions, access health records, and consult with pharmacists — all from their smartphones.',
    features: ['Online medicine ordering', 'Digital prescription upload', 'Health record management', 'Medicine reminders & refills', 'Virtual pharmacist consultation', 'Loyalty points & discounts'],
    gradient: 'from-blue-600 to-indigo-700',
  },
];

const Services = () => {
  useScrollReveal();
  return (
    <div className="">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
        <div className="absolute top-10 right-1/4 w-96 h-96 bg-secondary-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-10 w-72 h-72 bg-primary-400/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm font-semibold mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 inline-block"></span>
            Our Services &amp; Offerings
            <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 inline-block"></span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-5">
            Complete Healthcare <br /><span className="text-secondary-400">Solutions</span>
          </h1>
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-12 bg-white/30 rounded-full"></span>
            <span className="w-2 h-2 rounded-full bg-secondary-400"></span>
            <span className="h-px w-12 bg-white/30 rounded-full"></span>
          </div>
          <p className="text-white/75 text-base lg:text-lg max-w-3xl mx-auto leading-relaxed">
            APlus Pharmacy and APLUS Healthcare Solutions offer a comprehensive range of healthcare services — from retail pharmacy and generic medicines to diagnostics, home healthcare, and digital health platforms.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
              What We Offer
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">Our Complete</span>
              <span className="gradient-text">Healthcare Ecosystem</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">A comprehensive healthcare ecosystem built around the needs of every Indian family.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {serviceCategories.map((service, i) => (
              <div key={i} className="reveal bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className={`bg-gradient-to-r ${service.gradient} p-8 text-white`}>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold mb-4 ${service.badgeColor} bg-white/20 text-white border border-white/30`}>
                    {service.badge}
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-3">{service.title}</h3>
                  <p className="text-white/85 leading-relaxed">{service.desc}</p>
                </div>
                <div className="p-8">
                  <h4 className="font-semibold text-gray-900 mb-4">Key Features</h4>
                  <ul className="grid grid-cols-1 gap-3">
                    {service.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-secondary-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Products Range */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
              Product Range
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">Our Product</span>
              <span className="gradient-text">Categories</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-secondary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">10,000+ products across all major healthcare and wellness categories.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { name: 'Prescription Medicines', icon: '💊', count: '5000+' },
              { name: 'Generic Medicines', icon: '🏥', count: '2000+' },
              { name: 'OTC Products', icon: '🛒', count: '1500+' },
              { name: 'Nutritional Supplements', icon: '💪', count: '500+' },
              { name: 'Baby & Mother Care', icon: '👶', count: '300+' },
              { name: 'Skincare & Cosmetics', icon: '✨', count: '400+' },
              { name: 'Medical Devices', icon: '🩺', count: '200+' },
              { name: 'Herbal & Ayurvedic', icon: '🌿', count: '350+' },
            ].map((cat, i) => (
              <div key={i} className="reveal bg-gray-50 hover:bg-primary-50 border border-gray-100 hover:border-primary-200 rounded-2xl p-5 text-center transition-all duration-300 cursor-pointer group">
                <div className="text-3xl mb-3">{cat.icon}</div>
                <div className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-primary-700">{cat.name}</div>
                <div className="text-primary-600 text-xs font-bold">{cat.count} Products</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl font-bold text-white mb-4">Find an APlus Pharmacy Near You</h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">Visit your nearest APlus Pharmacy outlet or reach out to us for any healthcare needs.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/pharmacy" className="bg-white text-primary-700 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg">APlus Pharmacy Outlets</Link>
            <Link to="/contact" className="btn-outline">Contact Us <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
